import React, { useEffect, useMemo, useRef, useState } from 'react';
import { adminAPI } from '../services/api';

const MAX_FILES = 50;

function flattenAttributes(value, prefix = '', output = []) {
  if (value === null || value === undefined) {
    output.push({ key: prefix || 'value', value: String(value) });
    return output;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      output.push({ key: prefix || 'value', value: '[]' });
      return output;
    }
    value.forEach((item, idx) => {
      const key = prefix ? `${prefix}[${idx}]` : `[${idx}]`;
      flattenAttributes(item, key, output);
    });
    return output;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      output.push({ key: prefix || 'value', value: '{}' });
      return output;
    }
    entries.forEach(([key, nestedValue]) => {
      const nextPrefix = prefix ? `${prefix}.${key}` : key;
      flattenAttributes(nestedValue, nextPrefix, output);
    });
    return output;
  }

  output.push({ key: prefix || 'value', value: String(value) });
  return output;
}

function ModerationTester() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const resultPreviewUrlsRef = useRef([]);

  const previews = useMemo(
    () => selectedFiles.map((file) => ({ file, previewUrl: URL.createObjectURL(file) })),
    [selectedFiles]
  );

  useEffect(() => {
    return () => {
      previews.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
      resultPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > MAX_FILES) {
      alert(`You selected ${files.length} files. Only the first ${MAX_FILES} will be tested.`);
    }
    setSelectedFiles(files.slice(0, MAX_FILES));
  };

  const onTest = async () => {
    if (selectedFiles.length === 0) return;
    try {
      setLoading(true);
      const filesInOrder = [...selectedFiles];
      const res = await adminAPI.testCommunityModerationImage(selectedFiles);
      const runTests = (res.data?.tests || []).map((testItem) => {
        const fileForPreview = filesInOrder[testItem?.index];
        const previewUrl = fileForPreview ? URL.createObjectURL(fileForPreview) : null;
        if (previewUrl) {
          resultPreviewUrlsRef.current.push(previewUrl);
        }
        return { ...testItem, previewUrl };
      });
      const summary = res.data?.finalJsonSummary || {
        generatedAt: new Date().toISOString(),
        mode: 'test_only',
        totals: res.data?.totals || {},
        images: runTests,
      };
      setResults((prev) => [{ id: Date.now(), ...res.data, tests: runTests, finalJsonSummary: summary }, ...prev]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to run moderation test');
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async (payload) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      alert('Final JSON summary copied.');
    } catch (err) {
      alert('Failed to copy JSON summary.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-gray-900">Moderation Tester</h1>
        <p className="text-sm text-gray-600 mt-1">
          Upload up to 50 images and inspect full moderation metadata for each one. Test-only: no deletion, no user/post changes.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 border">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Select Images (max 50)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onFileChange}
              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onTest}
              disabled={selectedFiles.length === 0 || loading}
              className="px-4 py-2 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 disabled:opacity-50"
            >
              {loading ? `Testing ${selectedFiles.length} image(s)...` : `Run Moderation Test (${selectedFiles.length})`}
            </button>
            <span className="text-xs text-gray-500">
              {selectedFiles.length} selected / {MAX_FILES} max
            </span>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Previews</label>
            {previews.length === 0 ? (
              <div className="h-40 rounded-lg border bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
                No images selected
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {previews.map(({ file, previewUrl }, idx) => (
                  <div key={`${file.name}-${idx}`} className="rounded-lg border bg-gray-50 p-2">
                    <img src={previewUrl} alt={file.name} className="h-28 w-full object-cover rounded-md border" />
                    <div className="mt-2 text-[11px] text-gray-600 truncate" title={file.name}>
                      {file.name}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {file.type || 'unknown'} | {file.size} bytes
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {results.length === 0 ? (
          <div className="bg-white rounded-xl border p-4 text-sm text-gray-500">No tests yet.</div>
        ) : (
          results.map((run) => (
            <div key={run.id} className="bg-white rounded-2xl shadow border p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                  Batch Test
                </span>
                <span className="text-xs text-gray-500">tested: {run?.totals?.tested ?? 0}</span>
                <span className="text-xs text-green-700">approved: {run?.totals?.approved ?? 0}</span>
                <span className="text-xs text-amber-700">review: {run?.totals?.review ?? 0}</span>
                <span className="text-xs text-red-700">rejected: {run?.totals?.rejected ?? 0}</span>
              </div>

              {(run.tests || []).map((testItem, idx) => {
                const flattened = flattenAttributes(testItem);
                const decision = testItem?.decision?.decision || 'APPROVE';
                const decisionClassName = decision === 'REJECT'
                  ? 'bg-red-100 text-red-700'
                  : decision === 'REVIEW'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700';
                return (
                  <div key={`${run.id}-${idx}`} className="rounded-xl border p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${decisionClassName}`}>
                        {decision}
                      </span>
                      <span className="text-xs text-gray-500">{testItem?.file?.originalName}</span>
                      <span className="text-xs text-gray-500">{testItem?.file?.mimeType}</span>
                      <span className="text-xs text-gray-500">{testItem?.file?.sizeBytes} bytes</span>
                    </div>

                    {testItem?.previewUrl ? (
                      <div className="rounded-lg border bg-gray-50 p-2">
                        <img
                          src={testItem.previewUrl}
                          alt={testItem?.file?.originalName || `tested-image-${idx + 1}`}
                          className="max-h-64 w-full object-contain rounded-md border bg-white"
                        />
                      </div>
                    ) : null}

                    <div className="max-h-80 overflow-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2">Attribute</th>
                            <th className="text-left px-3 py-2">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flattened.map((row, rowIdx) => (
                            <tr key={`${row.key}-${rowIdx}`} className="border-t align-top">
                              <td className="px-3 py-2 font-mono text-[11px] text-gray-700">{row.key}</td>
                              <td className="px-3 py-2 break-all text-gray-900">{row.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-600">View raw JSON</summary>
                      <pre className="mt-2 bg-gray-50 border rounded-lg p-3 overflow-auto text-[11px] text-gray-800">
                        {JSON.stringify(testItem, null, 2)}
                      </pre>
                    </details>
                  </div>
                );
              })}

              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600">View batch raw JSON</summary>
                <pre className="mt-2 bg-gray-50 border rounded-lg p-3 overflow-auto text-[11px] text-gray-800">
                  {JSON.stringify(run, null, 2)}
                </pre>
              </details>

              <div className="rounded-xl border p-4 space-y-2 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">Final JSON Summary (All Images)</div>
                  <button
                    type="button"
                    onClick={() => copyJson(run.finalJsonSummary)}
                    className="px-3 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-md hover:bg-black"
                  >
                    Copy JSON
                  </button>
                </div>
                <pre className="max-h-96 overflow-auto text-[11px] text-gray-800 bg-white border rounded-lg p-3">
                  {JSON.stringify(run.finalJsonSummary, null, 2)}
                </pre>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ModerationTester;
